
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
import ByteDanceSvg from '~/svg/bytedance.svg';
// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------
const CheckIcon = () => (<svg className='h-4 w-4 text-amber-400 flex-shrink-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
  </svg>);
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='inline-block text-xs font-bold tracking-[0.2em] uppercase mb-4' style={{ color: '#fbbf24' }}>
    {children}
  </span>);
// ---------------------------------------------------------------------------
// Video examples
// ---------------------------------------------------------------------------
type VideoExample = {
    /** Absolute URL of the clip. Host these ourselves — see the note below. */
    src: string;
    /** Optional still shown before playback, so the tile is never blank. */
    poster?: string;
    /** Short title. Passed through t(); missing keys fall back to the English. */
    label: string;
    /** One-line description of what the take demonstrates. */
    description: string;
    /** Portrait sources letterbox inside the 16:9 tile instead of cropping. */
    portrait?: boolean;
};
/**
 * Official Seedance 2.5 demos from ByteDance's launch post
 * (seed.bytedance.com/en/blog/one-take-creation-flexible-referencing-
 * introducing-seedance-2-5), re-hosted on our CDN: downloaded, re-encoded
 * (≤1280w, CRF 28, faststart, AAC 96k) and uploaded with poster frames to the
 * R2 `images` bucket under `landing/seedance-2-5/`.
 *
 * To add another: re-encode + upload the clip and a poster frame the same way,
 * and append an entry — labels fall back to English until their translations
 * land. Never reuse a key that has already 404'd — Cloudflare caches those
 * negatively. A tile whose video errors removes itself, and the whole section
 * renders nothing if no clip is playable. Our own renders from the first
 * revision of this page are still on the CDN under the same prefix
 * (one-take-ad, vertical-agent, voice-dialogue, espresso-macro, sailboat-dawn,
 * bicycle-cat) if a mixed grid is ever wanted.
 */
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/seedance-2-5';
const videoExamples: VideoExample[] = [
    {
        src: `${SHOWCASE_CDN}/official-one-take-story.mp4`,
        poster: `${SHOWCASE_CDN}/official-one-take-story-poster.jpg`,
        label: '30-Second One-Take Story',
        description: 'A multi-shot story arc rendered as one continuous generation',
    },
    {
        src: `${SHOWCASE_CDN}/official-multi-shot-extension.mp4`,
        poster: `${SHOWCASE_CDN}/official-multi-shot-extension-poster.jpg`,
        label: 'Multi-Round Extension',
        description: 'A concert sequence extended shot after shot with characters held consistent',
    },
    {
        src: `${SHOWCASE_CDN}/official-camera-moves.mp4`,
        poster: `${SHOWCASE_CDN}/official-camera-moves-poster.jpg`,
        label: 'Seamless Camera Movement',
        description: 'Smooth transitions between camera moves with audio and visuals in sync',
    },
    {
        src: `${SHOWCASE_CDN}/official-multimodal-reference.mp4`,
        poster: `${SHOWCASE_CDN}/official-multimodal-reference-poster.jpg`,
        label: 'Multimodal References',
        description: 'Characters, scenes, and props pulled from reference files exactly as directed',
    },
    {
        src: `${SHOWCASE_CDN}/official-motion-reference.mp4`,
        poster: `${SHOWCASE_CDN}/official-motion-reference-poster.jpg`,
        label: 'Motion & Creative Reference',
        description: 'Reference clips drive the subjects, actions, and camera work in the frame',
    },
    {
        src: `${SHOWCASE_CDN}/official-green-screen-edit.mp4`,
        poster: `${SHOWCASE_CDN}/official-green-screen-edit-poster.jpg`,
        label: 'Timestamped Green-Screen Edit',
        description: 'A reference-to-video edit scoped second by second, straight from the prompt',
    },
];
const VideoShowcase = ({ items, t, }: {
    items: VideoExample[];
    t: (key: string) => string;
}) => {
    const [failed, setFailed] = useState<string[]>([]);
    const visible = items.filter((v) => !failed.includes(v.src));
    // Nothing playable — drop the section entirely rather than leaving a heading
    // above an empty grid.
    if (visible.length === 0)
        return null;
    return (<section className='py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center mb-16'>
          <SectionLabel>{t('Video Examples')}</SectionLabel>
          <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
            {t('See Seedance 2.5 in Action')}
          </h2>
          <p className='mt-6 text-lg text-gray-400'>
            {t('Every clip below is a single continuous take with audio generated alongside the picture — no stitching, no cuts to hide.')}
          </p>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5'>
          {visible.map((video) => (<div key={video.src} className='group relative'>
              <div className='relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]'>
                <video src={video.src} poster={video.poster} muted loop playsInline preload='metadata' className={`w-full aspect-video ${video.portrait
                ? 'object-contain bg-black/40'
                : 'object-cover'}`} onError={() => setFailed((prev) => prev.includes(video.src) ? prev : [...prev, video.src])} onMouseEnter={(e) => (e.target as HTMLVideoElement).play().catch(() => { })} onMouseLeave={(e) => {
                const v = e.target as HTMLVideoElement;
                v.pause();
                v.currentTime = 0;
            }}/>
                <div className='absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none'>
                  <div className='h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center'>
                    <svg className='h-5 w-5 text-white ml-0.5' fill='currentColor' viewBox='0 0 24 24'>
                      <path d='M8 5v14l11-7z'/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className='mt-3 px-1'>
                <h3 className='text-sm font-semibold text-white'>
                  {t(video.label)}
                </h3>
                <p className='mt-1 text-xs text-gray-500'>
                  {t(video.description)}
                </p>
              </div>
            </div>))}
        </div>
      </div>
    </section>);
};
// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Seedance25LandingPage() {
    const { t } = useLocale('seedance-2-5');
    // Beats of a single 30-second one-take, used by the timeline visual.
    const storyBeats = [
        { label: t('Setup'), range: '0–7s', pct: 23 },
        { label: t('Development'), range: '7–16s', pct: 30 },
        { label: t('Turning point'), range: '16–24s', pct: 27 },
        { label: t('Resolution'), range: '24–30s', pct: 20 },
    ];
    const unrestrictedPoints = [
        t('Your own photos work — selfies, portraits, team shots, headshots.'),
        t('Real footage works — upload a clip of a real person and edit it.'),
        t('Fully automatic — the reroute happens server-side, before you notice.'),
        t('No extra cost — a refused attempt is never billed. You pay once.'),
        t('No extra wait — the second provider starts the moment the first declines.'),
    ];
    const creationModes = [
        {
            mode: t('Text to Video'),
            tag: 'T2V',
            description: t('Write the scene and Seedance 2.5 renders up to 30 seconds of it in a single continuous take — with dialogue, foley, and ambience generated inside the same pass.'),
            details: [
                t('Prompts up to 5,000 characters (~1,000 words)'),
                t('Multi-shot storytelling inside one take'),
                t('All 6 aspect ratios — 16:9, 9:16, 1:1, 4:3, 3:4, 21:9'),
                t('4–30 second output duration'),
            ],
        },
        {
            mode: t('Image to Video'),
            tag: 'I2V',
            description: t('Animate a still into a take of up to 30 seconds. The subject, wardrobe, and lighting stay intact while the prompt drives motion, camera, and sound.'),
            details: [
                t('Identity-preserving animation from a single photo'),
                t('Optional end frame to lock the closing shot'),
                t('Framing follows your source image automatically'),
                t('Real people accepted — no face rejection'),
            ],
        },
        {
            mode: t('Reference to Video'),
            tag: 'R2V',
            description: t('Feed the model up to 30 reference images and 10 audio clips, then tell it what each one is for. Characters, products, storyboards, and keyframes stay consistent across the whole take.'),
            details: [
                t('Up to 30 reference images per generation'),
                t('Up to 10 reference audio clips (30s total)'),
                t('Character, object, and style reference roles'),
                t('@Image notation to bind each file to a job'),
            ],
        },
        {
            mode: t('Edit with AI'),
            tag: 'EDIT',
            description: t('Scrub to a moment in a finished clip, drag a time range, and describe the change. Seedance 2.5 rewrites only that window and leaves the rest of the take untouched.'),
            details: [
                t('Timestamp-scoped edits with in/out handles'),
                t('Add, remove, or replace objects mid-shot'),
                t('Attach up to 4 reference images as @Image1–@Image4'),
                t('Works on any 4–30 second clip you own'),
            ],
        },
    ];
    const tiers = [
        {
            name: t('Standard'),
            tagline: t('Maximum fidelity'),
            credits: t('12 credits / second at 720p'),
            points: [
                t('480p and 720p output'),
                t('Half price at 480p — 6 credits / second'),
                t('Best detail, texture, and motion stability'),
                t('Editing from 7 credits / second of source'),
            ],
            highlight: true,
        },
        {
            name: t('Turbo'),
            tagline: t('Roughly half the price'),
            credits: t('7 credits / second at 720p'),
            points: [
                t('720p and 1080p output'),
                t('The same 30-second takes and native audio'),
                t('Faster turnaround for drafts and iteration'),
                t('Editing from 9 credits / second of source'),
            ],
            highlight: false,
        },
    ];
    const referenceSlots = [
        {
            count: t('Up to 30 images'),
            role: t('Characters, products, locations, storyboards, style plates'),
        },
        {
            count: t('Up to 10 audio clips'),
            role: t('Voiceover, music, and sound effects — 30 seconds total'),
        },
        {
            count: t('Up to 50 inputs'),
            role: t('The model fuses every reference into one generation pass'),
        },
    ];
    const specs = [
        {
            label: t('Max Take Length'),
            value: '30s',
            sub: t('Single continuous shot'),
        },
        { label: t('Duration Range'), value: '4–30s', sub: t('Any whole second') },
        {
            label: t('Resolution'),
            value: '1080p',
            sub: t('480p · 720p · 1080p by tier'),
        },
        {
            label: t('Aspect Ratios'),
            value: '6',
            sub: '16:9 · 9:16 · 1:1 · 4:3 · 3:4 · 21:9',
        },
        {
            label: t('Reference Images'),
            value: '30',
            sub: t('Characters, objects, scenes'),
        },
        {
            label: t('Reference Audio'),
            value: '10',
            sub: t('30 seconds total max'),
        },
        {
            label: t('Multimodal Inputs'),
            value: '50',
            sub: t('Text · Image · Video · Audio'),
        },
        {
            label: t('Prompt Length'),
            value: '5,000',
            sub: t('Characters — around 1,000 words'),
        },
        {
            label: t('Audio Languages'),
            value: '11',
            sub: t('Native dialogue with lip-sync'),
        },
        {
            label: t('Audio Layers'),
            value: '3',
            sub: t('Dialogue · Foley · Ambience'),
        },
        {
            label: t('Timestamp Editing'),
            value: t('Yes'),
            sub: t('Edit a time range in place'),
        },
        {
            label: t('Face Restrictions'),
            value: t('None'),
            sub: t('Real people accepted'),
        },
    ];
    const comparisonItems = [
        {
            feature: t('Max take length'),
            v25: t('30 seconds in one pass'),
            v20: t('Up to 15 seconds'),
        },
        {
            feature: t('Multi-shot in one take'),
            v25: t('Yes — setup, development, turn, resolution'),
            v20: t('One beat per generation'),
        },
        {
            feature: t('Reference images'),
            v25: t('Up to 30'),
            v20: t('Up to 9'),
        },
        {
            feature: t('Reference audio'),
            v25: t('10 clips, 30 seconds total'),
            v20: t('3 clips, 15 seconds total'),
        },
        {
            feature: t('Multimodal inputs'),
            v25: t('Up to 50 per generation'),
            v20: t('Up to 12 per generation'),
        },
        {
            feature: t('Timestamp editing'),
            v25: t('Edit a time range without re-rendering'),
            v20: t('Not supported'),
        },
        {
            feature: t('Prompt adherence'),
            v25: t('Around 20% better'),
            v20: t('Baseline'),
        },
        {
            feature: t('Native audio'),
            v25: t('Co-generated with the picture, 11 languages'),
            v20: t('Dialogue, foley, and ambience'),
        },
        {
            feature: t('Fast tier'),
            v25: t('Turbo — half the price, up to 1080p'),
            v20: t('Fast and Mini tiers'),
        },
        {
            feature: t('Real people in references'),
            v25: t('Accepted — automatic provider fallback'),
            v20: t('Accepted — automatic provider fallback'),
        },
    ];
    const howItWorks = [
        {
            step: '01',
            title: t('Pick your starting point'),
            description: t('Open the Video Generator and choose Seedance 2.5 for a text prompt, Seedance 2.5 Image to animate a photo, or Seedance 2.5 Reference to work from up to 30 reference images.'),
        },
        {
            step: '02',
            title: t('Write the whole take'),
            description: t('You have 5,000 characters. Describe the beats in order — what opens the shot, what changes, what resolves — plus the camera moves and the soundscape for each stretch.'),
        },
        {
            step: '03',
            title: t('Set length and tier'),
            description: t('Choose any duration from 4 to 30 seconds, an aspect ratio, and a tier: Standard for maximum fidelity, or Turbo for roughly half the credits and 1080p output.'),
        },
        {
            step: '04',
            title: t('Generate, then edit in place'),
            description: t('When the take lands, hit Edit with AI, drag a time range over the moment you want changed, and describe the fix. Only that window is re-rendered.'),
        },
    ];
    const promptTips = [
        {
            title: t('Write the timeline, not a sentence'),
            description: t('Thirty seconds is long enough for a story. Lay the beats out in order with rough timings so the model knows what belongs where instead of averaging everything into one continuous motion.'),
            example: t('Opens on a locked-off wide of an empty workshop at dawn. Around five seconds in, a woman enters frame left and switches on the lamps one by one. Midway, she uncovers a finished guitar and turns it in the light. The take closes on a slow push into the headstock as the room fills with morning sun.'),
        },
        {
            title: t('Give every reference a job'),
            description: t('With up to 30 images in play, an unlabelled pile is a coin flip. Name each one in the prompt — who is the character, what is the product, which plate is only there for the colour grade.'),
            example: t('@Image1 is the presenter — keep her face and jacket exactly. @Image2 is the product; it must stay unchanged in every shot. @Image3 is a lighting and colour reference only — do not copy its composition.'),
        },
        {
            title: t('Direct the sound as well as the picture'),
            description: t('Audio is generated in the same pass, so it takes direction the same way. Call out the dialogue, the foley, and the room tone, and say when each one should come in.'),
            example: t('Rain on a tin roof throughout. He says "we are not going back" quietly at the halfway mark. Chair scrapes as he stands, then footsteps on wet concrete for the last five seconds. No music.'),
        },
        {
            title: t('Edit rather than regenerate'),
            description: t('If a take is 90% right, do not roll the dice on a fresh generation. Scope an edit to the seconds that are wrong — the rest of the shot, including the audio, stays exactly as it was.'),
            example: t('From 12s to 18s: remove the coffee cup from the desk and have him look toward the window instead of the camera. Leave everything else in the shot unchanged.'),
        },
    ];
    const useCases = [
        {
            title: t('Full Ads in One Take'),
            description: t('Thirty seconds is a broadcast spot. Hook, product, and payoff render as one continuous shot with native audio — no stitching, no drift between cuts.'),
            icon: '📺',
        },
        {
            title: t('Real-Person UGC'),
            description: t('Upload a photo of an actual person and animate them. Nothing is rejected for containing a face, so creator-style content works with real people, not just synthetic ones.'),
            icon: '🤳',
        },
        {
            title: t('Product Films'),
            description: t('Lock the product with reference images so it stays pixel-accurate across the whole take, then let the camera orbit, push, and rack focus around it.'),
            icon: '🛍️',
        },
        {
            title: t('Short-Form Narrative'),
            description: t('A setup, a turn, and a resolution inside a single generation. Consistent characters and continuous audio make 30 seconds enough for a real scene.'),
            icon: '🎞️',
        },
        {
            title: t('Fix-It Passes'),
            description: t('A near-perfect clip with one wrong detail no longer means starting over. Scope the change to a few seconds and keep everything you already liked.'),
            icon: '🩹',
        },
        {
            title: t('Explainers & Training'),
            description: t('Narration, on-screen action, and ambience arrive together in 11 languages, so a lesson or walkthrough is finished the moment the render is.'),
            icon: '📚',
        },
        {
            title: t('Music & Rhythm Cuts'),
            description: t('Feed up to 10 audio clips as reference and let the visuals move with them — beat-matched motion and transitions across the full 30 seconds.'),
            icon: '🎶',
        },
        {
            title: t('Storyboard to Screen'),
            description: t('Hand the model your boards as reference images and get an animated take that follows them shot for shot, at pre-visualisation speed.'),
            icon: '🎯',
        },
    ];
    const faqs = [
        {
            question: t('What is Seedance 2.5?'),
            answer: t("Seedance 2.5 is ByteDance's flagship video model, announced in June 2026. It generates up to 30 seconds of continuous, coherent video in a single pass — with dialogue, foley, and ambience produced alongside the picture rather than added afterwards. It accepts up to 50 multimodal references per generation and supports editing a finished clip by timestamp."),
        },
        {
            question: t('Does Seedance 2.5 block videos with real people in them?'),
            answer: t("Not on Easy-Peasy.AI. Seedance 2.5's primary provider refuses any reference image or video showing a recognisable real person, which rules out most real footage. We run every Seedance 2.5 job across two providers: when the first one declines your upload because it contains a person, we automatically resubmit it to a provider that accepts real people. It happens server-side, costs nothing extra, and needs no setting on your end."),
        },
        {
            question: t('So there are no rules at all?'),
            answer: t('There are. Unrestricted here means we do not block ordinary photos and footage of ordinary people — it does not mean anything goes. Deepfakes and impersonation of public figures, sexual content involving real people or minors, and anything unlawful remain prohibited under our Terms of Use, and provider-level filters on likeness-sensitive material still apply.'),
        },
        {
            question: t('How long can a Seedance 2.5 video be?'),
            answer: t('Any whole number of seconds from 4 to 30, in a single generation. That is a genuine one-take render, not several short clips joined together, so there is no seam or identity drift where the cuts would otherwise be. Longer pieces are built by generating several takes and assembling them in the video editor.'),
        },
        {
            question: t('What resolutions does Seedance 2.5 support?'),
            answer: t('The Standard tier renders 480p and 720p; the Turbo tier renders 720p and 1080p. Both tiers produce the same 30-second takes with native audio — Turbo trades a little fidelity for roughly half the credit cost and a faster turnaround.'),
        },
        {
            question: t('What is the difference between Standard and Turbo?'),
            answer: t('Standard costs 12 credits per second at 720p (6 at 480p) and is the higher-fidelity option. Turbo costs 7 credits per second at 720p, adds 1080p output, and returns results faster. Use Turbo to iterate and Standard for the take you are going to ship.'),
        },
        {
            question: t('How does timestamp editing work?'),
            answer: t('Open any Seedance-compatible clip you own, drag in and out handles over the seconds you want changed, and describe the change in plain language. You can attach up to four reference images and address them as @Image1 to @Image4. Only that time range is re-rendered — the rest of the take, audio included, is left alone. Source clips must be 4 to 30 seconds long.'),
        },
        {
            question: t('How many reference files can I use?'),
            answer: t('The Video Generator accepts up to 30 reference images and up to 10 reference audio clips (30 seconds combined) on Seedance 2.5 Reference. The model itself fuses up to 50 multimodal inputs into a single generation pass. Reference audio always has to be paired with at least one reference image.'),
        },
        {
            question: t('Does Seedance 2.5 generate audio?'),
            answer: t('Yes, and not as a second step. Dialogue, foley, and ambience are produced in the same pass as the picture, which is what makes lip-sync and action-matched sound effects line up frame for frame. Native dialogue covers 11 languages. You can also switch audio off if you want a silent take.'),
        },
        {
            question: t('How is Seedance 2.5 different from Seedance 2.0?'),
            answer: t('Takes are twice as long (30 seconds against 15), references jump from 9 images to 30, audio references from 3 clips to 10, and prompt adherence improves by roughly 20%. Seedance 2.5 also adds timestamp editing, which 2.0 does not have at all, and can carry a multi-beat story inside a single shot instead of one beat per generation.'),
        },
        {
            question: t('How many credits does Seedance 2.5 cost?'),
            answer: t('Pricing is per second of output. Standard is 12 credits per second at 720p and 6 at 480p; Turbo is 7 credits per second at 720p. Editing bills per second of the source clip — from 7 credits at 480p on Standard and 9 at 720p on Turbo. A 4-second 480p Standard take, the default, is 24 credits.'),
        },
        {
            question: t('Can I use Seedance 2.5 videos commercially?'),
            answer: t('Yes. Videos you generate with Seedance 2.5 on Easy-Peasy.AI can be used commercially — advertising, social media, product films, client work — subject to our terms of service.'),
        },
    ];
    return (<main className='relative overflow-hidden bg-[#0b0a12]'>
      <Seo templateTitle={t('Unrestricted Seedance 2.5 AI Video Generator | 30-Second Takes, Native Audio, Real Faces Allowed')} description={t('Generate 30-second one-take AI videos with Seedance 2.5 by ByteDance — native audio, up to 50 multimodal references, and timestamp editing. Unrestricted: we do not block photos or footage of real people.')} image='https://easy-peasy.ai/api/og-template?title=Unrestricted%20Seedance%202.5%20AI%20Video%20Generator'/>

      {/* ================================================================= */}
      {/* HERO                                                              */}
      {/* ================================================================= */}
      <div className='relative min-h-[700px] sm:min-h-[90vh] max-h-[1000px] w-full overflow-hidden'>
        {/* Background layers */}
        <div className='absolute inset-0'>
          {/* Hero video — a Seedance 2.5 Turbo render (8s, 1080p), muted loop */}
          <video autoPlay muted loop playsInline preload='metadata' poster={`${SHOWCASE_CDN}/hero-poster.jpg`} src={`${SHOWCASE_CDN}/hero.mp4`} className='absolute inset-0 h-full w-full object-cover'/>
          {/* Dark scrim so the copy stays readable over the video */}
          <div className='absolute inset-0 bg-[#0b0a12]/70'/>
          {/* Noise grain */}
          <div className='absolute inset-0 opacity-[0.035]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }}/>
          {/* Radial glow center */}
          <div className='absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2' style={{
            width: '900px',
            height: '600px',
            background: 'radial-gradient(ellipse, rgba(251,191,36,0.10) 0%, rgba(251,113,133,0.06) 40%, transparent 70%)',
        }}/>
          {/* Edge accents */}
          <div className='absolute -top-32 -left-32 w-96 h-96 rounded-full bg-amber-500/[0.07] blur-[100px]'/>
          <div className='absolute bottom-0 right-0 w-[500px] h-[400px] bg-violet-500/[0.07] blur-[120px]'/>
          {/* Film-strip line accents */}
          <div className='absolute top-0 left-[8%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
          <div className='absolute top-0 right-[8%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
        </div>

        {/* Navigation */}
        <div className='absolute top-0 left-0 w-full z-20'>
          <LandingPageNavigation variant='light'/>
        </div>

        {/* Hero content */}
        <div className='relative z-10 mx-auto max-w-7xl px-6 pt-36 sm:pt-52 lg:px-8 lg:pt-40 h-full flex flex-col justify-center'>
          <div className='mx-auto max-w-4xl text-center'>
            {/* Badge */}
            <div className='mb-10 flex justify-center'>
              <div className='inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 text-[13px] text-gray-400 backdrop-blur-sm'>
                <span className='flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white shadow-sm'>
                  <ByteDanceSvg className='h-5 w-5' aria-hidden='true'/>
                </span>
                {t("ByteDance's flagship video model")}
              </div>
            </div>

            {/* Title */}
            <h1 className='text-5xl font-extrabold tracking-tight text-white sm:text-8xl leading-[0.9]'>
              <span className='block text-2xl sm:text-4xl font-bold tracking-[0.15em] uppercase mb-3' style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #fb7185 60%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                {t('Unrestricted')}
              </span>
              <span className='block'>Seedance</span>
              <span className='block mt-1' style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #fb7185 50%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                2.5
              </span>
            </h1>

            <p className='mt-8 text-lg leading-relaxed text-gray-400 max-w-2xl mx-auto sm:text-xl'>
              {t('Thirty seconds of continuous video in one pass. Native audio generated with the picture. Up to 50 references per shot. And no face blocking — real people are welcome here.')}
            </p>

            {/* Availability badge */}
            <div className='mt-6 inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/[0.06] px-5 py-2.5'>
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75'/>
                <span className='relative inline-flex h-2 w-2 rounded-full bg-amber-400'/>
              </span>
              <span className='text-sm font-semibold text-amber-300'>
                {t('Available Now')}
              </span>
            </div>

            {/* CTA */}
            <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link href='/ai-videos' className='group relative inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-[#0b0a12] transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #fb7185 100%)',
        }}>
                {t('Start Creating with Seedance 2.5')}
                <span className='ml-2 transition-transform group-hover:translate-x-0.5'>
                  →
                </span>
              </Link>
              <a href='#unrestricted' className='text-sm font-medium text-gray-400 hover:text-white transition-colors'>
                {t('Why unrestricted?')}
              </a>
            </div>

            <div className='mt-8 flex justify-center'>
              <HappyUsers fromLandingPage={true} className='items-center'/>
            </div>

            {/* Stats row */}
            <div className='mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto'>
              {[
            { value: '30s', label: t('One-Take Length') },
            { value: '50', label: t('References') },
            { value: '11', label: t('Audio Languages') },
            { value: t('None'), label: t('Face Restrictions') },
        ].map((s) => (<div key={s.label} className='text-center rounded-xl border border-white/[0.06] bg-white/[0.02] py-4 px-3'>
                  <div className='text-2xl font-bold sm:text-3xl' style={{ color: '#fbbf24' }}>
                    {s.value}
                  </div>
                  <div className='mt-1 text-xs text-gray-500 uppercase tracking-wider'>
                    {s.label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className='absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0b0a12] to-transparent'/>
      </div>

      {/* Logos */}
      <LogosCloud />

      {/* ================================================================= */}
      {/* VIDEO EXAMPLES (renders only once real clips are listed)          */}
      {/* ================================================================= */}
      <VideoShowcase items={videoExamples} t={t}/>

      {/* ================================================================= */}
      {/* UNRESTRICTED — NO FACE BLOCKING                                    */}
      {/* ================================================================= */}
      <section id='unrestricted' className='relative py-24 sm:py-32 overflow-hidden' style={{
            background: 'linear-gradient(135deg, #16110c 0%, #120e1a 55%, #0b0a12 100%)',
        }}>
        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none' style={{
            width: '760px',
            height: '420px',
            background: 'radial-gradient(ellipse, rgba(251,191,36,0.10) 0%, transparent 62%)',
        }}/>

        <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            <div>
              <SectionLabel>{t('Unrestricted')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('No Face Blocking. Real People Allowed.')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-6'>
                {t("Seedance 2.5's primary provider refuses any reference image or video that shows a recognisable real person. Upload a photo of yourself, your team, or a customer on most platforms and the job comes back rejected before it renders a single frame.")}
              </p>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Easy-Peasy.AI runs every Seedance 2.5 generation across two providers. The moment the first one declines your footage for containing a person, we resubmit it to a provider that accepts real people. Same prompt, same settings, same price.')}
              </p>
              <ul className='space-y-4 mb-10'>
                {unrestrictedPoints.map((item) => (<li key={item} className='flex items-start gap-3 text-gray-300'>
                    <CheckIcon />
                    <span className='text-sm'>{item}</span>
                  </li>))}
              </ul>
              <ButtonLink href='/ai-videos' variant='light' className='rounded-xl bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-base font-semibold shadow-lg'>
                {t('Try It With Your Own Photo')}
              </ButtonLink>
            </div>

            {/* Routing visual */}
            <div className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('What happens to a photo of a real person')}
              </div>

              <div className='space-y-4'>
                {/* Elsewhere */}
                <div className='rounded-xl border border-rose-500/20 bg-rose-500/[0.05] p-5'>
                  <div className='text-xs font-bold uppercase tracking-wider text-rose-300 mb-3'>
                    {t('Elsewhere')}
                  </div>
                  <div className='flex items-center gap-3 text-sm text-gray-400'>
                    <span className='rounded-lg bg-white/[0.05] px-2.5 py-1 text-xs font-medium text-gray-300'>
                      {t('Your upload')}
                    </span>
                    <span className='text-gray-600'>→</span>
                    <span className='rounded-lg bg-white/[0.05] px-2.5 py-1 text-xs font-medium text-gray-300'>
                      {t('Provider A')}
                    </span>
                    <span className='text-gray-600'>→</span>
                    <span className='rounded-lg bg-rose-500/15 px-2.5 py-1 text-xs font-semibold text-rose-300'>
                      {t('Rejected')}
                    </span>
                  </div>
                  <p className='mt-3 font-mono text-[11px] leading-relaxed text-rose-300/70'>
                    {t('"the input may contain a real person"')}
                  </p>
                </div>

                {/* Here */}
                <div className='rounded-xl border border-amber-400/25 bg-amber-400/[0.06] p-5'>
                  <div className='text-xs font-bold uppercase tracking-wider text-amber-300 mb-3'>
                    {t('On Easy-Peasy.AI')}
                  </div>
                  <div className='flex flex-wrap items-center gap-2.5 text-sm text-gray-400'>
                    <span className='rounded-lg bg-white/[0.05] px-2.5 py-1 text-xs font-medium text-gray-300'>
                      {t('Your upload')}
                    </span>
                    <span className='text-gray-600'>→</span>
                    <span className='rounded-lg bg-white/[0.05] px-2.5 py-1 text-xs font-medium text-gray-300'>
                      {t('Provider A')}
                    </span>
                    <span className='text-gray-600'>→</span>
                    <span className='rounded-lg bg-white/[0.05] px-2.5 py-1 text-xs font-medium text-gray-300'>
                      {t('Auto-reroute')}
                    </span>
                    <span className='text-gray-600'>→</span>
                    <span className='rounded-lg bg-amber-400/20 px-2.5 py-1 text-xs font-semibold text-amber-200'>
                      {t('Rendered')}
                    </span>
                  </div>
                  <p className='mt-3 text-[11px] leading-relaxed text-amber-200/70'>
                    {t('You never see the first refusal, and it is never billed.')}
                  </p>
                </div>
              </div>

              {/* Honest caveat */}
              <div className='mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5'>
                <div className='text-xs font-bold uppercase tracking-wider text-gray-400 mb-2'>
                  {t('What is still off-limits')}
                </div>
                <p className='text-xs text-gray-500 leading-relaxed'>
                  {t('Unrestricted means ordinary photos of ordinary people are not blocked — not that anything goes. Deepfakes and impersonation of public figures, sexual content involving real people or minors, and anything unlawful stay prohibited under our Terms of Use, and provider-level filters on likeness-sensitive material still apply.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* THE 30-SECOND ONE-TAKE                                            */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            <div>
              <SectionLabel>{t('One-Take Creation')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('Thirty Seconds. One Continuous Shot.')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-6'>
                {t('Most video models cap out between five and fifteen seconds, so anything longer means generating pieces and stitching them together — and paying for it in visual drift, mismatched lighting, and characters who quietly change face between cuts.')}
              </p>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Seedance 2.5 renders the whole thirty seconds in a single pass. Because it is one take, it can carry a real story arc — a setup, a development, a turning point, and a resolution — with the same character, the same room, and the same continuous soundtrack throughout.')}
              </p>
              <div className='grid grid-cols-2 gap-4'>
                {[
            { v: '30s', l: t('In a single generation') },
            { v: '4–30s', l: t('Any whole second') },
            { v: '0', l: t('Cuts to hide') },
            { v: '1', l: t('Continuous soundtrack') },
        ].map((x) => (<div key={x.l} className='rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
                    <div className='text-2xl font-bold' style={{ color: '#fbbf24' }}>
                      {x.v}
                    </div>
                    <div className='mt-1 text-xs text-gray-500'>{x.l}</div>
                  </div>))}
              </div>
            </div>

            {/* Timeline visual */}
            <div className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('A story arc inside one shot')}
              </div>

              {/* Bar */}
              <div className='flex h-3 w-full overflow-hidden rounded-full bg-white/[0.04]'>
                {storyBeats.map((beat, i) => (<div key={beat.label} style={{
                width: `${beat.pct}%`,
                background: [
                    'linear-gradient(90deg, #fbbf2455, #fbbf24cc)',
                    'linear-gradient(90deg, #fb923c55, #fb923ccc)',
                    'linear-gradient(90deg, #fb718555, #fb7185cc)',
                    'linear-gradient(90deg, #a78bfa55, #a78bfacc)',
                ][i],
            }}/>))}
              </div>

              {/* Scale */}
              <div className='mt-2 flex justify-between text-[10px] font-mono text-gray-600'>
                <span>0s</span>
                <span>10s</span>
                <span>20s</span>
                <span>30s</span>
              </div>

              {/* Beats */}
              <div className='mt-8 space-y-3'>
                {storyBeats.map((beat, i) => (<div key={beat.label} className='flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3'>
                    <span className='h-2.5 w-2.5 flex-shrink-0 rounded-full' style={{
                background: [
                    '#fbbf24',
                    '#fb923c',
                    '#fb7185',
                    '#a78bfa',
                ][i],
            }}/>
                    <span className='text-sm font-semibold text-white'>
                      {beat.label}
                    </span>
                    <span className='ml-auto font-mono text-xs text-gray-500'>
                      {beat.range}
                    </span>
                  </div>))}
              </div>

              <p className='mt-6 text-xs leading-relaxed text-gray-500'>
                {t('One generation, one identity, one soundtrack — the arc plays out inside the shot instead of across a sequence of clips.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* CREATION MODES                                                    */}
      {/* ================================================================= */}
      <section id='creation-modes' className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Creation Modes')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Four Ways to Work')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Start from a prompt, a photo, or a pile of references — and once a take exists, change it by timestamp instead of rolling the dice again.')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {creationModes.map((mode) => (<div key={mode.tag} className='group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-all hover:border-amber-500/25 hover:bg-white/[0.04]'>
                <div className='flex items-start gap-4 mb-5'>
                  <span className='inline-flex items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-bold tracking-wider text-amber-300'>
                    {mode.tag}
                  </span>
                  <h3 className='text-xl font-bold text-white'>{mode.mode}</h3>
                </div>
                <p className='text-gray-400 leading-relaxed mb-6'>
                  {mode.description}
                </p>
                <ul className='space-y-2.5'>
                  {mode.details.map((d) => (<li key={d} className='flex items-start gap-2.5 text-sm text-gray-400'>
                      <CheckIcon />
                      <span>{d}</span>
                    </li>))}
                </ul>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* TIMESTAMP EDITING                                                 */}
      {/* ================================================================= */}
      <section className='relative py-24 sm:py-32 overflow-hidden' style={{
            background: 'linear-gradient(135deg, #120e1a 0%, #0f1020 50%, #0b0a12 100%)',
        }}>
        <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            {/* Editor mock */}
            <div className='order-2 lg:order-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('Edit with AI')}
              </div>

              {/* Track */}
              <div className='relative h-14 w-full overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.03]'>
                {/* selection */}
                <div className='absolute inset-y-0 rounded-md border-x-2 border-amber-400/70 bg-amber-400/15' style={{ left: '38%', width: '24%' }}/>
                {/* frame ticks */}
                <div className='absolute inset-0 flex'>
                  {Array.from({ length: 24 }).map((_, i) => (<div key={i} className='flex-1 border-r border-white/[0.04]'/>))}
                </div>
              </div>
              <div className='mt-2 flex justify-between font-mono text-[10px] text-gray-600'>
                <span>0s</span>
                <span className='text-amber-300'>12s → 18s</span>
                <span>30s</span>
              </div>

              {/* Instruction */}
              <div className='mt-6 rounded-xl border border-white/[0.06] bg-white/[0.03] p-4'>
                <div className='text-[10px] font-bold tracking-wider uppercase text-gray-600 mb-2'>
                  {t('Your instruction')}
                </div>
                <p className='font-mono text-xs leading-relaxed text-gray-300'>
                  {t('From 12s to 18s: replace the mug with @Image1 and keep the lighting as it is.')}
                </p>
              </div>

              {/* Result */}
              <div className='mt-4 flex items-center gap-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4'>
                <CheckIcon />
                <span className='text-xs text-amber-200'>
                  {t('Six seconds re-rendered. The other 24 untouched.')}
                </span>
              </div>
            </div>

            <div className='order-1 lg:order-2'>
              <SectionLabel>{t('Post-Generation Editing')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('Fix a Moment, Not the Whole Clip')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Until now, one wrong detail in an otherwise perfect take meant generating again and hoping. Seedance 2.5 edits by timestamp: drag a range over the seconds that are wrong, describe the change, and only that window is re-rendered.')}
              </p>
              <ul className='space-y-4 mb-10'>
                {[
            t('Add, remove, or replace objects inside a time range'),
            t('Attach up to 4 reference images as @Image1–@Image4'),
            t('Everything outside the range stays byte-for-byte the same'),
            t('Works on any clip you own between 4 and 30 seconds'),
            t('Standard and Turbo editing tiers, priced per source second'),
        ].map((item) => (<li key={item} className='flex items-start gap-3 text-gray-300'>
                    <CheckIcon />
                    <span className='text-sm'>{item}</span>
                  </li>))}
              </ul>
              <ButtonLink href='/ai-videos' variant='light' className='rounded-xl bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-base font-semibold shadow-lg'>
                {t('Try It Now')}
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* MULTIMODAL REFERENCING                                            */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center'>
            <div>
              <SectionLabel>{t('Flexible Referencing')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6'>
                {t('Up to 50 References. One Pass.')}
              </h2>
              <p className='text-lg text-gray-400 leading-relaxed mb-8'>
                {t('Seedance 2.0 accepted twelve files. Seedance 2.5 fuses up to fifty multimodal inputs into a single generation — enough to pin a cast, a product, a location, a storyboard, and a colour grade all at once, and have every one of them survive the full thirty seconds.')}
              </p>
              <div className='space-y-4'>
                {referenceSlots.map((item) => (<div key={item.count} className='flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
                    <span className='text-sm font-bold text-amber-300 whitespace-nowrap min-w-[130px]'>
                      {item.count}
                    </span>
                    <span className='text-sm text-gray-400'>{item.role}</span>
                  </div>))}
              </div>
            </div>

            {/* Reference diagram */}
            <div className='relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
              <div className='text-xs font-bold tracking-wider uppercase text-gray-500 mb-6'>
                {t('Reference Roles')}
              </div>

              <div className='space-y-3 mb-8'>
                {[
            {
                label: '@Image1 — Character',
                color: '#fbbf24',
                width: '92%',
            },
            {
                label: '@Image2 — Product',
                color: '#fb923c',
                width: '84%',
            },
            {
                label: '@Image3 — Location',
                color: '#fb7185',
                width: '72%',
            },
            {
                label: '@Image4 — Storyboard',
                color: '#f472b6',
                width: '66%',
            },
            {
                label: '@Image5 — Colour grade',
                color: '#c084fc',
                width: '58%',
            },
            {
                label: '@Audio1 — Voiceover',
                color: '#a78bfa',
                width: '78%',
            },
        ].map((slot) => (<div key={slot.label} className='flex items-center gap-3'>
                    <span className='text-xs text-gray-500 w-44 text-right whitespace-nowrap hidden sm:block'>
                      {slot.label}
                    </span>
                    <span className='text-xs text-gray-500 w-24 text-right whitespace-nowrap sm:hidden'>
                      {slot.label.split(' — ')[0]}
                    </span>
                    <div className='flex-1 h-2.5 rounded-full bg-white/[0.04] overflow-hidden'>
                      <div className='h-full rounded-full' style={{
                width: slot.width,
                background: `linear-gradient(90deg, ${slot.color}40, ${slot.color}bb)`,
            }}/>
                    </div>
                  </div>))}
              </div>

              <div className='flex items-center justify-center my-6'>
                <div className='w-px h-8 bg-gradient-to-b from-amber-500/40 to-amber-500/10'/>
              </div>

              <div className='rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 text-center'>
                <div className='text-sm font-bold text-amber-300'>
                  {t('One 30-second take with native audio')}
                </div>
                <div className='text-xs text-gray-500 mt-1'>
                  {t('Every reference held consistent from first frame to last')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* NATIVE AUDIO                                                      */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Native Audio')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Sound Made With the Picture')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Audio and video are produced in the same pass rather than bolted together afterwards, which is why lip movement, footsteps, and room tone land on the right frame.')}
            </p>
          </div>

          <div className='mx-auto max-w-5xl grid grid-cols-1 gap-5 sm:grid-cols-3'>
            {[
            {
                layer: t('Dialogue'),
                desc: t('Lip-synced speech in 11 languages, generated frame-accurately with the mouth that speaks it.'),
                color: '#fbbf24',
            },
            {
                layer: t('Foley'),
                desc: t('Footsteps, impacts, handling noise, and object interactions matched to the action on screen.'),
                color: '#fb7185',
            },
            {
                layer: t('Ambience'),
                desc: t('Room tone, weather, traffic, and crowds that hold steady across the whole thirty seconds.'),
                color: '#a78bfa',
            },
        ].map((item) => (<div key={item.layer} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6'>
                <span className='inline-block h-2.5 w-2.5 rounded-full mb-4' style={{ background: item.color }}/>
                <h3 className='text-lg font-bold text-white mb-2'>
                  {item.layer}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed'>
                  {item.desc}
                </p>
              </div>))}
          </div>

          <p className='mt-8 text-center text-sm text-gray-500'>
            {t('Prefer a silent take? Audio can be switched off per generation.')}
          </p>
        </div>
      </section>

      {/* ================================================================= */}
      {/* TIERS                                                             */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Tiers')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Standard or Turbo')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Both tiers render the same 30-second takes with native audio. Iterate on Turbo, ship on Standard — or stay on Turbo when you want 1080p.')}
            </p>
          </div>

          <div className='mx-auto max-w-4xl grid grid-cols-1 gap-6 sm:grid-cols-2'>
            {tiers.map((tier) => (<div key={tier.name} className={`rounded-2xl border p-8 ${tier.highlight
                ? 'border-amber-400/25 bg-amber-400/[0.05]'
                : 'border-white/[0.06] bg-white/[0.02]'}`}>
                <h3 className='text-xl font-bold text-white'>{tier.name}</h3>
                <p className='mt-1 text-sm text-gray-500'>{tier.tagline}</p>
                <div className='mt-5 text-lg font-bold' style={{ color: '#fbbf24' }}>
                  {tier.credits}
                </div>
                <ul className='mt-6 space-y-3'>
                  {tier.points.map((p) => (<li key={p} className='flex items-start gap-2.5 text-sm text-gray-400'>
                      <CheckIcon />
                      <span>{p}</span>
                    </li>))}
                </ul>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* HOW IT WORKS                                                      */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Workflow')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('How It Works')}
            </h2>
          </div>

          <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {howItWorks.map((step) => (<div key={step.step} className='relative'>
                <div className='text-5xl font-black mb-4' style={{ color: 'rgba(251,191,36,0.15)' }}>
                  {step.step}
                </div>
                <h3 className='text-lg font-bold text-white mb-3'>
                  {step.title}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed'>
                  {step.description}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* PROMPT GUIDE                                                      */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Prompt Guide')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Writing for a 30-Second Take')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('A prompt that works for a five-second clip will underfill thirty seconds. These four habits do most of the work.')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {promptTips.map((tip) => (<div key={tip.title} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8'>
                <h3 className='text-lg font-bold text-white mb-3'>
                  {tip.title}
                </h3>
                <p className='text-sm text-gray-400 leading-relaxed mb-5'>
                  {tip.description}
                </p>
                <div className='rounded-xl bg-white/[0.03] border border-white/[0.06] p-4'>
                  <div className='text-[10px] font-bold tracking-wider uppercase text-gray-600 mb-2'>
                    {t('Example Prompt')}
                  </div>
                  <p className='text-sm text-gray-300 leading-relaxed italic'>
                    &ldquo;{tip.example}&rdquo;
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* TECHNICAL SPECS                                                   */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Specifications')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Technical Details')}
            </h2>
          </div>

          <div className='mx-auto max-w-5xl grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
            {specs.map((spec) => (<div key={spec.label} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5'>
                <div className='text-xs text-gray-500 uppercase tracking-wider'>
                  {spec.label}
                </div>
                <div className='text-2xl font-bold mt-2' style={{ color: '#fbbf24' }}>
                  {spec.value}
                </div>
                <div className='text-xs text-gray-500 mt-1'>{spec.sub}</div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* VERSION COMPARISON                                                */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('Comparison')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('2.5 vs 2.0')}
            </h2>
            <p className='mt-6 text-lg text-gray-400'>
              {t('Twice the length, more than triple the references, and a way to fix a take instead of regenerating it.')}
            </p>
          </div>

          <div className='mx-auto max-w-4xl overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='border-b border-white/[0.08]'>
                  <th className='py-4 px-4 text-left text-sm font-medium text-gray-500'>
                    {t('Feature')}
                  </th>
                  <th className='py-4 px-4 text-left text-sm font-bold text-amber-300'>
                    {t('Seedance 2.5')}
                  </th>
                  <th className='py-4 px-4 text-left text-sm font-medium text-gray-600'>
                    {t('Seedance 2.0')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((item, i) => (<tr key={item.feature} className={i % 2 === 0 ? 'bg-white/[0.01]' : 'bg-transparent'}>
                    <td className='py-3.5 px-4 text-sm font-medium text-gray-300'>
                      {item.feature}
                    </td>
                    <td className='py-3.5 px-4 text-sm text-gray-200'>
                      <span className='inline-flex items-center gap-2'>
                        <CheckIcon />
                        {item.v25}
                      </span>
                    </td>
                    <td className='py-3.5 px-4 text-sm text-gray-600'>
                      {item.v20}
                    </td>
                  </tr>))}
              </tbody>
            </table>
          </div>

          <div className='mt-12 text-center'>
            <Link href='/ai-video-generator/seedance-2' className='text-sm font-medium text-gray-400 underline decoration-white/20 underline-offset-4 hover:text-white transition-colors'>
              {t('Read about Seedance 2.0 →')}
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================= */}
      {/* USE CASES                                                         */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-20'>
            <SectionLabel>{t('Use Cases')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('What You Can Create')}
            </h2>
          </div>

          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
            {useCases.map((uc) => (<div key={uc.title} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-amber-500/25 hover:bg-white/[0.04]'>
                <span className='text-2xl'>{uc.icon}</span>
                <h3 className='mt-3 text-base font-bold text-white'>
                  {uc.title}
                </h3>
                <p className='mt-2 text-sm text-gray-400 leading-relaxed'>
                  {uc.description}
                </p>
              </div>))}
          </div>

          <div className='mt-16 flex items-center justify-center'>
            <ButtonLink href={HAS_FREE_PLAN ? '/ai-videos' : '/#pricing'} variant='dark' className='px-8 py-3 text-lg'>
              {t('Start Creating with Seedance 2.5')}
            </ButtonLink>
          </div>
        </div>
      </section>

      {/* Shared sections on light background */}
      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      {/* ================================================================= */}
      {/* FAQ                                                               */}
      {/* ================================================================= */}
      <section className='py-24 sm:py-32 border-t border-white/[0.04]'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <SectionLabel>{t('FAQ')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Frequently Asked Questions')}
            </h2>
            <p className='mt-4 text-lg text-gray-400'>
              {t('Everything you need to know about Seedance 2.5')}
            </p>
          </div>

          <div className='mx-auto max-w-3xl space-y-4'>
            {faqs.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.06] bg-white/[0.02]'>
                <summary className='flex cursor-pointer items-center justify-between p-5 text-base font-semibold text-white'>
                  {faq.question}
                  <svg className='h-5 w-5 text-gray-600 transition-transform group-open:rotate-180 flex-shrink-0 ml-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                  </svg>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm text-gray-400 leading-relaxed'>
                    {faq.answer}
                  </p>
                </div>
              </details>))}
          </div>
        </div>
      </section>

      {/* CTA & Footer on default bg */}
      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos'/>
        <Footer />
      </div>
    </main>);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a11b03d551__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/seedance-2-5',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03d551__ as getStaticProps }
  